import { QueryClient } from '@tanstack/react-query';

/**
 * @file queryClient.ts
 * @description QueryClient **factory** (no module-level singleton).
 *
 * Why a factory and not a shared instance:
 *   A module-scope `new QueryClient()` is evaluated once per server process.
 *   Under SSR that single cache is shared by every concurrent request, so one
 *   user's fetched data can be served into another user's HTML. It is the
 *   canonical SSR data-leak. The React Query docs require one client per
 *   request on the server and one per browser tab on the client.
 *
 * Defaults are tuned for read-heavy UIs:
 * - `retry: 1` — a single fast retry recovers from transient hiccups
 *   without blocking the UI for ~7s (which the previous `retry: 3` +
 *   exponential backoff caused when any of the home page's ~10 parallel
 *   RPCs flaked).
 * - Fixed 800ms backoff — predictable and short. Per-query overrides
 *   remain available for endpoints that legitimately need longer waits.
 * - Mutations keep `retry: 1` (writes should not silently re-fire).
 *
 * Usage:
 *   - React tree: `const [client] = useState(() => makeQueryClient())`
 *     (see `src/App.tsx`). Never import a shared instance.
 *   - Imperative browser-only code: `getBrowserQueryClient()`. It throws on
 *     the server, so it can never create or reuse a server-global cache.
 */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        retry: 1,
        retryDelay: 800,
      },
      mutations: {
        retry: 1,
      },
    },
  });
}

/**
 * Browser-only shared client, for imperative consumers that genuinely cannot
 * reach the React tree (e.g. a non-React event handler that must invalidate).
 *
 * Hard-gated on `window`: on the server this THROWS rather than lazily
 * creating a module-global cache, which is what makes the leak structurally
 * impossible instead of merely discouraged.
 *
 * There are currently zero callers — prefer `useQueryClient()` inside React.
 */
let browserQueryClient: QueryClient | undefined;

export function getBrowserQueryClient(): QueryClient {
  if (typeof window === 'undefined') {
    throw new Error(
      'getBrowserQueryClient() is browser-only. On the server, create a ' +
        'per-request client with makeQueryClient() instead.',
    );
  }
  browserQueryClient ??= makeQueryClient();
  return browserQueryClient;
}

/** Test-only: drops the browser-scoped instance so specs stay isolated. */
export function __resetBrowserQueryClientForTests(): void {
  browserQueryClient = undefined;
}
